import { first } from 'lodash';
import { common } from '@gosupersimple/types';

import { createBasePipeline, isBasePipeline, isPipelineWithParent } from '@/core/pipeline';
import { getCellByPipelineIdOrThrow } from '@/core/cell/cells';

import {
  CohortOperation,
  Exploration,
  Field,
  Fields,
  Model,
  Pipeline,
  PipelineStateRelation,
} from '../types';
import { EditCohortOptions, EditCohortState } from './types';
import { FieldGroup, flattenPipeline } from '../pipeline/utils';
import { getFinalState, isInvalidModel, PipelineStateContext } from '../pipeline/state';
import { getJoinKeyToSemanticModel, getRelationToSemanticModel } from '../model/utils';
import { createFieldOptionGrouped } from '../components/select-options/field';
import { createFieldOption } from '../components/select-options/field';
import { getValidRelationTypesForOperation } from '../edit-pipeline/utils/relation';

export type TimeKeyReference = {
  fieldKey: string;
  relation?: { key: string; modelId: string };
};

export const getCohortOperation = (pipeline: Pipeline): CohortOperation | undefined => {
  const operation = first(pipeline.operations);

  if (operation?.operation !== 'cohort') {
    return undefined;
  }

  return operation;
};

export const getDefaultIdFieldKey = (fields: Fields, model: Model, models: Model[]) => {
  return (
    fields.find((field) => field.key === getJoinKeyToSemanticModel(model, models, 'User'))?.key ??
    fields.find((field) => field.key === getJoinKeyToSemanticModel(model, models, 'Account'))
      ?.key ??
    fields.find((field) => Boolean(field.pk))?.key ??
    first(fields.filter((field) => field.key.toLowerCase().endsWith('id')))?.key ??
    first(fields)?.key
  );
};

export const getDefaultEventTimeKey = (fields: Fields, model: Model): TimeKeyReference => ({
  fieldKey:
    fields.find((field) => field.key === model.semantics?.properties?.createdAt)?.key ??
    fields.find((field) => field.type === 'Date')?.key ??
    first(fields)?.key ??
    '',
});

export const getDefaultCohortTimeKey = (
  model: Model,
  fields: Fields,
  models: Model[],
): TimeKeyReference => {
  const validRelationTypes = getValidRelationTypesForOperation('addRelatedColumn');
  const userRelation = getRelationToSemanticModel(model, models, 'User', validRelationTypes);
  const accountRelation = getRelationToSemanticModel(model, models, 'Account', validRelationTypes);
  const userModel = models.find((model) => model.modelId === userRelation?.modelId);
  const accountModel = models.find((model) => model.modelId === accountRelation?.modelId);
  const userCreatedAtField = userModel?.properties.find(
    (property) => property.key === userModel.semantics?.properties?.createdAt,
  );
  const accountCreatedAtField = accountModel?.properties.find(
    (property) => property.key === accountModel.semantics?.properties?.createdAt,
  );
  if (userRelation !== null && userCreatedAtField !== undefined) {
    return {
      fieldKey: userCreatedAtField.key,
      relation: { key: userRelation.key, modelId: model.modelId },
    };
  }
  if (accountRelation !== null && accountCreatedAtField !== undefined) {
    return {
      fieldKey: accountCreatedAtField.key,
      relation: { key: accountRelation.key, modelId: model.modelId },
    };
  }
  return {
    fieldKey: fields.find((field) => field.type === 'Date')?.key ?? first(fields)?.key ?? '',
  };
};

export const getIdFields = (fields: Fields) => fields.filter(({ type }) => type !== 'Date');
export const getTimeKeyFields = (fields: Fields) => fields.filter(({ type }) => type === 'Date');

const getTimeKeyFieldsFromRelation = (
  relation: PipelineStateRelation,
  getModel: (modelId: string) => Model,
): Fields => {
  const timeKeyFields = getTimeKeyFields(getModel(relation.modelId).properties);
  return timeKeyFields.map((field) => ({
    ...field,
    relation: {
      key: relation.key,
      modelId: relation.modelId,
      baseModelId: relation.baseModelId,
      name: relation.name,
    },
  }));
};

export const getCohortPipelineState = (
  pipeline: Pipeline | undefined,
  exploration: Exploration,
  ctx: PipelineStateContext,
) => {
  if (pipeline === undefined) {
    return undefined;
  }
  const { baseModelId, operations } = flattenPipeline(pipeline, exploration);
  const parentOperations = operations.slice(0, operations.length - pipeline.operations.length);
  return getFinalState(createBasePipeline({ baseModelId, operations: parentOperations }), ctx);
};

export const getAllTimeKeyFields = (
  fields: Fields,
  pipeline: Pipeline | undefined,
  relations: PipelineStateRelation[],
  getModel: (modelId: string) => Model,
): (Field | FieldGroup)[] => {
  if (pipeline === undefined) {
    return [];
  }

  return [
    ...getTimeKeyFields(fields),
    ...relations
      .filter((relation) =>
        getValidRelationTypesForOperation('addRelatedColumn').includes(relation.type),
      )
      .map((relation) => ({
        key: relation.key,
        name: `on ${relation.name}`,
        fields: getTimeKeyFieldsFromRelation(relation, getModel),
      })),
  ];
};

export const buildOptions = (
  idFields: Fields,
  cohortTimeKeyFields: (Field | FieldGroup)[],
  eventTimeKeyFields: (Field | FieldGroup)[],
  pipeline: Pipeline | undefined,
  exploration: Exploration,
  model: Model | undefined,
): EditCohortOptions | undefined => {
  if (pipeline === undefined) {
    return;
  }

  const modelName = model === undefined || isInvalidModel(model) ? undefined : model.name;

  if (isBasePipeline(pipeline)) {
    return {
      title: modelName,
      idFields: idFields.map(createFieldOption),
      cohortTimeKeyFields: cohortTimeKeyFields.map(createFieldOptionGrouped),
      eventTimeKeyFields: eventTimeKeyFields.map(createFieldOptionGrouped),
    };
  }

  if (isPipelineWithParent(pipeline)) {
    const parentCell = getCellByPipelineIdOrThrow(exploration.view.cells, pipeline.parentId);

    return {
      title: ('title' in parentCell ? parentCell.title : undefined) ?? modelName,
      idFields: idFields.map(createFieldOption),
      cohortTimeKeyFields: cohortTimeKeyFields.map(createFieldOptionGrouped),
      eventTimeKeyFields: eventTimeKeyFields.map(createFieldOptionGrouped),
    };
  }
};

/**
 * Reverse-lookup the field option reference for a `state.cohortTimeKey` / `state.eventTimeKey`.
 * If the key was produced by an `addRelatedColumn` operation, the matching option lives in the
 * relation's group keyed by the original (related-model) fieldKey, so we follow the operation back
 * to find it.
 */
export const getTimeKeyFieldReference = (
  timeKey: string | undefined,
  pipeline: Pipeline | undefined,
): { fieldKey: string; relation?: { key: string; modelId?: string } } | undefined => {
  if (timeKey === undefined || pipeline === undefined) {
    return undefined;
  }
  const operation = pipeline.operations.find(
    (op) =>
      op.operation === 'addRelatedColumn' &&
      op.parameters.columns.some((column) => column.property.key === timeKey),
  );
  if (operation === undefined || operation.operation !== 'addRelatedColumn') {
    return { fieldKey: timeKey };
  }

  const matchingColumn = operation.parameters.columns.find(
    (column) => column.property.key === timeKey,
  );

  if (matchingColumn === undefined) {
    return { fieldKey: timeKey };
  }

  return {
    fieldKey: matchingColumn.key,
    relation: operation.parameters.relation,
  };
};

export const generateRelationTimeKeyColumnKey = (reference: {
  baseModelId: string;
  relationKey: string;
  fieldKey: string;
}) => `${reference.baseModelId}_${reference.relationKey}_${reference.fieldKey}`;

export const updateCohortTimeKeys = (
  state: EditCohortState,
  timeKeys: { cohortTimeKey?: TimeKeyReference; eventTimeKey?: TimeKeyReference },
): EditCohortState => {
  const { pipeline } = state;
  if (pipeline === undefined) {
    return state;
  }

  const updatedState = Object.entries(timeKeys).reduce(
    (state, [timeKey, reference]): EditCohortState => {
      if (reference === undefined) {
        return state;
      }

      if (reference.relation === undefined) {
        return { ...state, [timeKey]: reference.fieldKey };
      }

      const columnKey = generateRelationTimeKeyColumnKey({
        baseModelId: reference.relation.modelId,
        relationKey: reference.relation.key,
        fieldKey: reference.fieldKey,
      });

      if (timeKeyColumnExists(state, columnKey)) {
        return { ...state, [timeKey]: columnKey };
      }

      return {
        ...state,
        [timeKey]: columnKey,
        pipeline: {
          ...state.pipeline,
          operations: [
            ...(state.pipeline?.operations ?? []),
            {
              operation: 'addRelatedColumn',
              parameters: {
                relation: { key: reference.relation.key, modelId: reference.relation.modelId },
                columns: [
                  {
                    key: reference.fieldKey,
                    property: {
                      key: columnKey,
                      name: timeKey,
                    },
                  },
                ],
              },
            },
          ],
        } as Pipeline,
      };
    },
    state,
  );

  return removeUnusedTimeKeyRelatedColumns(updatedState);
};

const timeKeyColumnExists = (state: EditCohortState, columnKey: string) => {
  const existingOperation = state.pipeline?.operations.find((operation) => {
    return (
      operation.operation === 'addRelatedColumn' &&
      operation.parameters.columns.some((column) => column.property.key === columnKey)
    );
  });

  return existingOperation !== undefined;
};

const removeUnusedTimeKeyRelatedColumns = (state: EditCohortState): EditCohortState => {
  if (state.pipeline === undefined) {
    return state;
  }

  const timeKeys = [state.cohortTimeKey, state.eventTimeKey];

  return {
    ...state,
    pipeline: {
      ...state.pipeline,
      operations: state.pipeline.operations.filter(
        (operation) =>
          operation.operation !== 'addRelatedColumn' ||
          operation.parameters.columns.some((column) => timeKeys.includes(column.property.key)),
      ),
    },
  };
};

const cohortTimeIntervalLabels: { [key in common.CohortTimeInterval]: string } = {
  year: 'Year',
  month: 'Month',
  week: 'Week',
  day: 'Day',
};

export const cohortTimeIntervalOptions = Object.entries(cohortTimeIntervalLabels).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

export const isStateReady = (state: EditCohortState): state is Required<EditCohortState> =>
  state.pipeline !== undefined &&
  state.cohortId !== undefined &&
  state.cohortTimeKey !== undefined &&
  state.cohortTimeInterval !== undefined &&
  state.eventTimeKey !== undefined &&
  state.eventTimeInterval !== undefined;
